import camelize from 'camelize';
import urlJoin from 'url-join';
import { parseISO } from 'date-fns';

const mergePreviewOptions = templateOptions => {
  if (window.PREVIEW_TEMPLATE_OPTIONS) {
    // Only include keys that already exist in the source object
    const validOpts = Object.entries(camelize(window.PREVIEW_TEMPLATE_OPTIONS)).reduce((result, [key, val]) => {
      if (templateOptions.hasOwnProperty(key)) result[key] = val;
      return result;
    }, {});
    return { ...templateOptions, ...validOpts };
  }
  return templateOptions;
};

const normalizeAppData = data => {
  let { menuItems, ...restData } = camelize(data);
  menuItems = (menuItems || []).map(({ uid, structuredContent, ...rest }) => {
    const sc = normalizeStructuredContent(structuredContent || []);
    return {
      id: uid,
      structuredContent: sc,
      ...rest,
    };
  });

  if (data.type === 'Player') {
    const { items, templateOptions, ...rest } = camelize(restData);
    const byId = items.reduce((result, item) => ({ ...result, [item.uid]: item }), {});
    const ordered = items.map(({ uid }) => uid);

    return {
      templateOptions: mergePreviewOptions(templateOptions),
      menuItems,
      project: { ...rest },
      items: { byId, ordered },
    };
  } else if (data.type === 'Website') {
    const { sections: sectionsData, templateOptions, useCustomNav, navConfig, ...rest } = camelize(restData);

    const sections = sectionsData.reduce((result, section) => {
      result.byId[section.uid] = section;
      result.ordered.push(section.uid);
      return result;
    }, { byId: {}, ordered: [] });

    const items = sectionsData.reduce((result, section) => {
      result.bySection[section.uid] = [];
      section.items.forEach(item => {
        result.bySection[section.uid].push(item.uid);
        result.byId[item.uid] = item;
      });
      return result;
    }, { bySection: {}, byId: {} });

    return {
      templateOptions: mergePreviewOptions(templateOptions),
      menuItems,
      useCustomNav,
      navConfig,
      project: { ...rest },
      sections,
      items,
    };
  }
};

const appDataUrl = urlJoin(window.BASE_PATH || '/', 'data', 'main.json');

export const getAppData = (requestCb, successCb, failureCb) => {
  requestCb();

  fetch(appDataUrl, { credentials: 'include' })
    .then(response => {
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response;
    })
    .then(response => response.json())
    .then(data => normalizeAppData(data))
    .then(data => successCb(data))
    .catch(err => {
      failureCb(err);
      console.error(err);
    });
};

export const getImageByWidth = (imageList = [], minWidth) => {
  // Sort array by width from smallest to largest, then return the first
  // item width width at least equal to minWidth. If no items match,
  // return the largest available.
  const imagesSorted = [...imageList].sort((a, b) => a.width - b.width);
  const [largest] = imagesSorted.slice(-1);
  return imagesSorted.find(({ width }) => width >= minWidth) || largest;
};

export const normalizeMediaObject = (media, imgWidth = 1500, thumbnailWidth = 200) => {
  const sourceType = media.type === 'video' ? media.source : 'htmlAudio';
  const source = media.source === 'file' ? media.versions[0].url : media.metadata.sourceUrl; // TODO
  const posterFrameImg = media.previewImages && getImageByWidth(media.previewImages.posterFrame, imgWidth);
  const posterFrame = posterFrameImg && posterFrameImg.url;
  const thumbnailImg = media.previewImages && getImageByWidth(media.previewImages.horizontalThumbnail, thumbnailWidth);
  const thumbnail = thumbnailImg && thumbnailImg.url;

  return {
    id: media.uid,
    type: media.type,
    sourceType,
    source,
    title: media.name,
    description: media.longDescription,
    credit: media.credit,
    transcript: media.transcript,
    posterFrame,
    thumbnail,
    durationSeconds: media.duration,
  };
};

export const normalizeStructuredContent = sc => sc.map(({ uid, ...rest }) => {
  let out = { id: uid, ...rest };
  if (out.blockType === 'image_grid') {
    out.items = out.items.map(({ uid, media, label, linkUrl }) => ({
      id: uid,
      label,
      linkUrl,
      imageUrl: getImageByWidth(media.versions, 600).url,
    }));
  } else if (out.blockType === 'media') {
    const { media, ...rest } = out;
    const { type, sourceType, source, durationSeconds } = normalizeMediaObject(out.media);
    const aspectRatio = media.width && media.height ? media.width / media.height : undefined;
    out = { ...rest, type, sourceType, source, durationSeconds, aspectRatio };
  }
  return out;
});

export const scInjectDynamicText = (sc = [], item = {}, subItem, project = {}) => sc.map(block => {
  let sourceItem = block.useSubItems && subItem ? subItem : item;
  switch (block.blockType) {
    case 'dynamic_text': {
      const sourceMap = {
        item__name: sourceItem.name,
        item__short_description: sourceItem.shortDescription,
        item__long_description: sourceItem.longDescription,
        item__transcript: sourceItem.transcript,
        release__name: project.name,
        release__short_description: project.shortDescription,
        release__long_description: project.longDescription,
      };
      const out = {
        id: block.id,
        blockType: 'text',
        index: block.index,
        content: sourceMap[block.source] || '',
      };
      if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(block.textStyle)) {
        out.blockType = 'heading';
        out.headingStyle = block.textStyle;
      }
      return out;
    }
    case 'related_links': {
      if (block.source === 'release') sourceItem = project;
      const items = (sourceItem.relatedLinks || []).map(({ images, ...rest }) => {
        const image = getImageByWidth(images || [], 200);
        return {
          ...rest,
          imageUrl: image ? image.url : '',
        };
      });
      return { ...block, items };
    }
    case 'credits': {
      if (sourceItem.creditType === 'simple') {
        return {
          id: block.id,
          blockType: 'text',
          index: block.index,
          content: sourceItem.credit || '',
        };
      }
      const items = sourceItem.credits.map(({ uid, contributor, role }) => {
        const { images, uid: cid, ...contributorAttrs } = contributor;
        const image = getImageByWidth(images || [], 200);
        return {
          ...contributorAttrs,
          uid,
          role,
          imageUrl: image ? image.url : '',
        };
      });
      return { ...block, items };
    }
    case 'publication_dates': {
      const items = [];
      const { displayPublished, publishedLabelText, displayUpdated, updatedLabelText, dateFormat, displayTime, ...rest } = block;
      if (block.displayPublished && project.publishedDate) {
        items.push({
          label: block.publishedLabelText,
          date: parseISO(project.publishedDate),
        });
      }
      if (block.displayUpdated && project.updatedDate) {
        items.push({
          label: block.updatedLabelText,
          date: parseISO(project.updatedDate),
        });
      }
      return {
        ...rest,
        items,
        format: block.dateFormat,
        showTime: block.displayTime,
      };
    }
    default:
      return block;
  }
});
